import { useRef } from 'react';
import { LazyMotion, domAnimation, m, AnimatePresence } from 'motion/react';
import { Tooltip } from '@caddie/ui-kit/components/tooltip';
import { useSuperbarTheme } from './hooks/useSuperbarTheme';
import { cn } from './utils/styles';
import type { VoiceModeState } from './hooks/useVoiceMode';
import type { AudioAnalyserService } from './services/audio-analyser.service';
import { useWaveformAnimation } from './hooks/useWaveformAnimation';
import { CADDIE_Z_INDEX } from '../constants/z-index';
import {
  WAVEFORM_VIEWBOX,
  WAVEFORM_BAR_WIDTH,
  WAVEFORM_BAR_GAP,
  WAVEFORM_BAR_MAX_HEIGHT,
  WAVEFORM_BAR_SCALES,
  WAVEFORM_ICON_SIZE,
  PILL_BAR_COUNT,
  PILL_BAR_WIDTH_PX,
  PILL_BAR_GAP_PX,
  PILL_BAR_MAX_HEIGHT_PX,
  ANIM_HALF_MIN,
  ANIM_HALF_MAX,
  PILL_DOT_COUNT,
} from './constants/waveform';

function WaveformIcon({ size, barFill }: { size: number; barFill: string }) {
  const cx = WAVEFORM_VIEWBOX / 2;
  const totalBarsWidth =
    WAVEFORM_BAR_SCALES.length * WAVEFORM_BAR_WIDTH +
    (WAVEFORM_BAR_SCALES.length - 1) * WAVEFORM_BAR_GAP;
  const startX = cx - totalBarsWidth / 2;

  return (
    <svg
      className="caddie-waveform-icon"
      width={size}
      height={size}
      viewBox={`0 0 ${WAVEFORM_VIEWBOX} ${WAVEFORM_VIEWBOX}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {WAVEFORM_BAR_SCALES.map((scale, i) => {
        const barX = startX + i * (WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_GAP);
        const barCenterX = barX + WAVEFORM_BAR_WIDTH / 2;
        return (
          <rect
            key={i}
            className={`caddie-waveform-bar caddie-waveform-bar-${i}`}
            x={barX}
            y={(WAVEFORM_VIEWBOX - WAVEFORM_BAR_MAX_HEIGHT) / 2}
            width={WAVEFORM_BAR_WIDTH}
            height={WAVEFORM_BAR_MAX_HEIGHT}
            rx={WAVEFORM_BAR_WIDTH / 2}
            fill={barFill}
            style={{
              transformOrigin: `${barCenterX}px ${cx}px`,
              transform: `scaleY(${scale})`,
            }}
          />
        );
      })}
    </svg>
  );
}

type VoiceModeButtonProps = {
  voiceModeState: VoiceModeState;
  onClick: () => void;
  analyserService?: AudioAnalyserService | null;
  size?: number;
  disabled?: boolean;
  disabledTooltip?: string;
};

export function VoiceModeButton({
  voiceModeState,
  onClick,
  analyserService,
  size = WAVEFORM_ICON_SIZE,
  disabled = false,
  disabledTooltip,
}: VoiceModeButtonProps) {
  const theme = useSuperbarTheme();
  const isActive = voiceModeState !== 'off';
  const barsRef = useRef<HTMLDivElement>(null);
  useWaveformAnimation(
    barsRef,
    analyserService ?? null,
    voiceModeState,
    PILL_BAR_COUNT,
    ANIM_HALF_MIN,
    ANIM_HALF_MAX
  );

  return (
    <LazyMotion features={domAnimation}>
      <AnimatePresence mode="wait" initial={false}>
        {isActive ? (
          <m.button
            key="waveform-pill"
            type="button"
            onClick={onClick}
            aria-label="Stop voice mode"
            initial={{ opacity: 0, scale: 0.85, width: 0 }}
            animate={{ opacity: 1, scale: 1, width: 'auto' }}
            exit={{ opacity: 0, scale: 0.85, width: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'caddietw-border-0 caddietw-appearance-none caddietw-cursor-pointer',
              'caddietw-flex caddietw-items-center caddietw-gap-3',
              'caddietw-rounded-full caddietw-px-4 caddietw-py-2',
              'caddietw-bg-red-500 hover:caddietw-bg-red-600',
              'caddietw-overflow-hidden'
            )}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <div
              className="caddie-pill-content"
              style={
                {
                  '--pill-bar-width': `${PILL_BAR_WIDTH_PX}px`,
                  '--pill-bar-gap': `${PILL_BAR_GAP_PX}px`,
                  '--pill-bar-height': `${PILL_BAR_MAX_HEIGHT_PX}px`,
                  '--pill-bar-initial-height': `${ANIM_HALF_MIN * 2}px`,
                } as React.CSSProperties
              }
            >
              {voiceModeState === 'processing' ? (
                <div key="dots" className="caddie-processing-dots">
                  {Array.from({ length: PILL_DOT_COUNT }, (_, i) => (
                    <div key={i} className="caddie-processing-dot" />
                  ))}
                </div>
              ) : (
                <div key="bars" ref={barsRef} className="caddie-waveform-bars">
                  {Array.from({ length: PILL_BAR_COUNT }, (_, i) => (
                    <div key={i} className="caddie-waveform-bar-pill" />
                  ))}
                </div>
              )}
            </div>
            <span className="caddietw-text-white caddietw-text-sm caddietw-font-medium caddietw-whitespace-nowrap">
              Stop
            </span>
          </m.button>
        ) : (
          <Tooltip
            label={
              disabled
                ? (disabledTooltip ?? 'Voice mode unavailable')
                : 'Voice mode'
            }
            position="top"
            withArrow
            zIndex={CADDIE_Z_INDEX.MENU}
          >
            <m.button
              key="waveform"
              type="button"
              onClick={disabled ? undefined : onClick}
              disabled={disabled}
              aria-label={disabled ? 'Voice mode blocked' : 'Start voice mode'}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: disabled ? 0.4 : 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.16, ease: 'easeOut' }}
              className={cn(
                'caddietw-border-0 caddietw-appearance-none caddietw-p-0',
                'caddietw-rounded-full caddietw-transition-colors',
                disabled
                  ? 'caddietw-cursor-not-allowed'
                  : 'caddietw-cursor-pointer'
              )}
              style={{ backgroundColor: theme.colors.ink.primary }}
              {...(disabled
                ? {}
                : { whileHover: { scale: 1.1 }, whileTap: { scale: 0.95 } })}
            >
              <WaveformIcon size={size} barFill={theme.colors.ink.inverse} />
            </m.button>
          </Tooltip>
        )}
      </AnimatePresence>
    </LazyMotion>
  );
}
